// ============================================================================
// Componente: FinanceiroNumerarioSap.js
// Objetivo: montar o "Lançamento contábil manual" do numerário no formato do
//           SAP, para o Financeiro conferir e transcrever.
//
// ⚠️ ISTO NÃO INTEGRA COM O SAP. Não envia nada, não reserva numeração. Número,
//   Nº origem e Nº transação são gerados lá no momento do lançamento — o portal
//   não tem como saber, e inventar seria pior que deixar em branco.
//
// O layout imita a tela do SAP de propósito (cabeçalho + grade Débito/Crédito),
// para quem transcreve não precisar traduzir nada: bate campo com campo.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
    FileSpreadsheet, Loader2, AlertTriangle, CheckCircle2, Copy, CalendarDays,
    Minus, Plus, RotateCcw, Download,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
    COR, COR_CLARA, COR_BORDA, COR_NEUTRA, COR_ALERTA,
    dinheiro, dataBR, diaSemana, mesDe, rotuloMes, rotuloSemana, parFiltro, th, td, tdNum,
} from './numerarioFormato';

const FinanceiroNumerarioSap = () => {
    const [semanas, setSemanas] = useState([]);
    const [selecionada, setSelecionada] = useState('');
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);
    const [copiado, setCopiado] = useState(null);
    // Escolha de período POR LOJA: { FER: 2 }. Vazio = usa a sugestão que o
    // motor detectou pelos envelopes do malote.
    const [periodoPorLoja, setPeriodoPorLoja] = useState({});
    // A coleta em foco. ⚠️ SEMPRE tem uma — não existe "todas": cada coleta é um
    // lote de lançamentos que o contador transcreve junto, e misturar duas numa
    // tela (e num arquivo) entrega mais do que ele conferiu de uma vez.
    const [coletaFiltro, setColetaFiltro] = useState('');
    // Mês (AAAA-MM) que recorta a lista de semanas — ela cresce a cada importação.
    const [mes, setMes] = useState('');

    const token = () => localStorage.getItem('token') || localStorage.getItem('authToken');

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch('/api/numerario/semanas', {
                    headers: { Authorization: `Bearer ${token()}` },
                });
                if (!res.ok) return;
                const lista = await res.json();
                setSemanas(lista);
                if (lista.length) {
                    setMes(mesDe(lista[0].semana));
                    setSelecionada(String(lista[0].semana).slice(0, 10));
                }
            } catch { /* o seletor fica vazio; a tela avisa */ }
        })();
    }, []);

    const carregar = useCallback(async (dt, escolha) => {
        if (!dt) return;
        setCarregando(true);
        setErro(null);
        try {
            const override = Object.entries(escolha || {})
                .map(([lj, n]) => `${lj}:${n}`).join(',');
            const res = await fetch(
                `/api/numerario/sap/${dt}${override ? `?semanas=${encodeURIComponent(override)}` : ''}`,
                { headers: { Authorization: `Bearer ${token()}` } },
            );
            const json = await res.json();
            if (!res.ok) { setErro(json.erro || 'Falha ao montar o lançamento.'); setDados(null); return; }
            setDados(json);
        } catch {
            setErro('Não foi possível falar com o servidor.');
            setDados(null);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(selecionada, periodoPorLoja); }, [selecionada, periodoPorLoja, carregar]);

    // Trocar de coleta zera a escolha manual — o período de uma coleta não diz
    // nada sobre o da outra.
    useEffect(() => { setPeriodoPorLoja({}); }, [selecionada]);

    // As semanas do mês escolhido — é esta lista que alimenta o seletor de semana.
    const semanasDoMes = semanas.filter((s) => mesDe(s.semana) === mes);
    const meses = [...new Set(semanas.map((s) => mesDe(s.semana)))];

    // Trocar de mês leva para a semana mais recente dele.
    useEffect(() => {
        if (!mes) return;
        const doMes = semanas.filter((s) => mesDe(s.semana) === mes);
        if (doMes.length && !doMes.some((s) => String(s.semana).slice(0, 10) === selecionada)) {
            setSelecionada(String(doMes[0].semana).slice(0, 10));
        }
    }, [mes, semanas, selecionada]);

    const mudarSemanas = (loja, novo) => setPeriodoPorLoja((p) => ({ ...p, [loja]: Math.max(1, Math.min(novo, 8)) }));
    const voltarSugestao = (loja) => setPeriodoPorLoja((p) => {
        const copia = { ...p };
        delete copia[loja];
        return copia;
    });

    // Copia as linhas separadas por TAB — cola direto em planilha, que é como o
    // operador leva para o SAP.
    const copiar = async (lanc) => {
        const texto = lanc.linhas.map((l) => [
            l.conta || '', l.nome,
            l.debito ? l.debito.toFixed(2).replace('.', ',') : '',
            l.credito ? l.credito.toFixed(2).replace('.', ',') : '',
            l.observacao, l.regraDistribuicao || '', lanc.cabecalho.filial,
        ].join('\t')).join('\n');
        try {
            await navigator.clipboard.writeText(texto);
            setCopiado(lanc.loja);
            setTimeout(() => setCopiado(null), 2000);
        } catch { /* sem permissão de área de transferência — segue sem copiar */ }
    };

    // ⚠️ O filtro de coleta recorta a TELA e também a EXPORTAÇÃO — o arquivo sai
    //   com o que está sendo visto. Exportar a semana inteira com a tela
    //   mostrando um malote só seria entregar mais do que o contador conferiu.
    const todosLancamentos = dados?.lancamentos || [];
    const lancamentos = coletaFiltro
        ? todosLancamentos.filter((l) => l.dtColeta === coletaFiltro)
        : todosLancamentos;
    // As coletas desta semana, deduzidas dos próprios lançamentos (a rota do SAP
    // devolve a data em cada um) — não precisa de uma segunda fonte.
    const coletasDaSemana = [...new Set(todosLancamentos.map((l) => l.dtColeta))].sort()
        .map((dt) => ({ dtColeta: dt, lojas: todosLancamentos.filter((l) => l.dtColeta === dt).map((l) => l.loja) }));

    // Sempre há uma coleta em foco. O padrão é a de MAIS lojas — a da quarta,
    // que é o grosso da rede; abrir numa coleta de uma loja só pareceria erro.
    useEffect(() => {
        if (!coletasDaSemana.length) { setColetaFiltro(''); return; }
        if (!coletasDaSemana.some((c) => c.dtColeta === coletaFiltro)) {
            const maior = coletasDaSemana.reduce((a, b) => (b.lojas.length > a.lojas.length ? b : a), coletasDaSemana[0]);
            setColetaFiltro(maior.dtColeta);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- coletasDaSemana é derivada de `dados` a cada render; depender dela criaria laço
    }, [dados, coletaFiltro]);

    // ── Exportar TODAS as lojas da coleta de uma vez (.xlsx) ─────────────────
    // O "Copiar linhas" resolve uma loja por vez; numa coleta de nove lojas isso
    // são nove idas e voltas. Este botão entrega a coleta inteira num arquivo só,
    // no mesmo espírito do Template SAP do RTB12.
    //
    // São DUAS abas de propósito:
    //   • "Lançamentos" — linha a linha, plana, para transcrever/colar. Sem linha
    //     de total no meio: total misturado com lançamento é o que estraga a
    //     colagem no SAP.
    //   • "Conferência" — uma linha por loja com os totais, se fecha e os avisos.
    //     É onde o "NÃO FECHA" da tela sobrevive à exportação; sem ela, o arquivo
    //     sairia com cara de conferido mesmo quando a tela grita para não
    //     transcrever.
    //
    // ⚠️ Débito e crédito vão como NÚMERO, não texto formatado — o Financeiro
    //   soma a coluna na planilha para bater com o relatório da Prosegur, e
    //   "1.234,56" como texto não soma.
    const exportarExcel = () => {
        if (!lancamentos.length) return;

        const linhas = lancamentos.flatMap((lanc) => lanc.linhas.map((l) => ({
            Loja: lanc.loja,
            // ⚠️ A coleta é coluna própria porque a semana pode ter mais de uma
            //   (quarta, sexta do MAL, terça do MOX). Sem ela, a planilha mistura
            //   malotes de dias diferentes sem dizer.
            Coleta: dataBR(lanc.dtColeta),
            Filial: lanc.cabecalho.filial,
            'Data de lançamento': dataBR(lanc.cabecalho.dataLancamento),
            'Série': lanc.cabecalho.serie,
            Origem: lanc.cabecalho.origem,
            'Tipo lançamento ECD': lanc.cabecalho.tipoLancamentoEcd,
            'Cta.contáb./cód.PN': l.conta || '',
            'Cta.cont./Nome PN': l.nome,
            'Débito': l.debito ?? null,
            'Crédito': l.credito ?? null,
            'Observações': l.observacao,
            'Regra distr.': l.regraDistribuicao || '',
        })));

        const conferencia = lancamentos.map((lanc) => ({
            Loja: lanc.loja,
            Coleta: dataBR(lanc.dtColeta),
            Filial: lanc.cabecalho.filial,
            'Semanas de numerário': lanc.semanasUsadas,
            'Total débito': lanc.totais.debito,
            'Total crédito': lanc.totais.credito,
            'Fecha?': lanc.fecha ? 'OK' : 'NÃO FECHA — não transcreva',
            'Avisos': (lanc.avisos || []).join(' | '),
        }));

        const wb = XLSX.utils.book_new();
        const wsLanc = XLSX.utils.json_to_sheet(linhas);
        wsLanc['!cols'] = [
            { wch: 6 }, { wch: 11 }, { wch: 12 }, { wch: 18 }, { wch: 10 }, { wch: 8 }, { wch: 20 },
            { wch: 18 }, { wch: 38 }, { wch: 14 }, { wch: 14 }, { wch: 34 }, { wch: 12 },
        ];
        XLSX.utils.book_append_sheet(wb, wsLanc, 'Lançamentos');

        const wsConf = XLSX.utils.json_to_sheet(conferencia);
        wsConf['!cols'] = [
            { wch: 6 }, { wch: 11 }, { wch: 12 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 28 }, { wch: 70 },
        ];
        XLSX.utils.book_append_sheet(wb, wsConf, 'Conferência');

        // O nome carrega a coleta quando o filtro está ativo: dois arquivos da
        // mesma semana, um por malote, não podem sair com o mesmo nome.
        const sufixo = coletaFiltro ? `_coleta_${coletaFiltro}` : '';
        XLSX.writeFile(wb, `Lancamentos_SAP_Numerario_${selecionada}${sufixo}.xlsx`);
    };

    // Coleta com alguma loja desbalanceada: o arquivo sai, mas avisado. Bloquear
    // seria pior — o contador usa justamente a exportação para investigar.
    const algumNaoFecha = lancamentos.some((l) => !l.fecha);

    return (
        <div style={{ padding: 24, maxWidth: 1280, margin: '0 auto' }}>
            <header style={{ marginBottom: 18 }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, color: COR, margin: 0 }}>
                    <FileSpreadsheet size={22} /> Lançamento contábil (SAP)
                </h2>
                <p style={{ color: '#475569', marginTop: 6, fontSize: 14, maxWidth: 900 }}>
                    O lançamento de cada loja da coleta, pronto para conferir e transcrever.
                    O portal <strong>não lança no SAP</strong> — número, origem e transação são gerados lá.
                </p>
            </header>

            <section style={{
                background: COR_CLARA, border: `1px solid ${COR_BORDA}`, borderRadius: 12,
                padding: 16, marginBottom: 20, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
            }}>
                <span style={parFiltro}>
                <label htmlFor="mes" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    fontWeight: 600, color: COR, fontSize: 14,
                }}>
                    <CalendarDays size={17} /> Período
                </label>
                <select id="mes" value={mes} onChange={(e) => setMes(e.target.value)}
                    style={{
                        padding: '8px 12px', borderRadius: 8, border: `1px solid ${COR_BORDA}`,
                        fontSize: 14, background: '#fff', color: '#0f172a', minWidth: 140,
                    }}>
                    {meses.length === 0 && <option value="">—</option>}
                    {meses.map((m) => <option key={m} value={m}>{rotuloMes(m)}</option>)}
                </select>
                </span>

                <span style={parFiltro}>
                <label htmlFor="coleta" style={{ fontWeight: 600, color: COR, fontSize: 14 }}>
                    Semana
                </label>
                <select id="coleta" value={selecionada} onChange={(e) => setSelecionada(e.target.value)}
                    style={{
                        padding: '8px 12px', borderRadius: 8, border: `1px solid ${COR_BORDA}`,
                        fontSize: 14, background: '#fff', color: '#0f172a', maxWidth: 460,
                    }}>
                    {semanasDoMes.length === 0 && <option value="">Nenhum relatório importado</option>}
                    {semanasDoMes.map((s) => {
                        const d = String(s.semana).slice(0, 10);
                        return <option key={d} value={d}>{rotuloSemana(s)}</option>;
                    })}
                </select>
                </span>

                {/* Filtro de coleta — ⚠️ SEM "todas": transcrever um malote por vez
                    é o fluxo real do contador, cada coleta vira um lote de
                    lançamentos no SAP, e a exportação segue o que está na tela. */}
                {coletasDaSemana.length > 1 && (
                    <span style={parFiltro}>
                        <label htmlFor="coleta-filtro" style={{ fontWeight: 600, color: COR, fontSize: 14 }}>
                            Coleta
                        </label>
                        <select id="coleta-filtro" value={coletaFiltro}
                            onChange={(e) => setColetaFiltro(e.target.value)}
                            style={{
                                padding: '8px 12px', borderRadius: 8, border: `1px solid ${COR_BORDA}`,
                                fontSize: 14, background: '#fff', color: '#0f172a', minWidth: 230,
                            }}>
                            {coletasDaSemana.map((c) => (
                                <option key={c.dtColeta} value={c.dtColeta}>
                                    {diaSemana(c.dtColeta)} {dataBR(c.dtColeta)} · {c.lojas.length <= 2 ? c.lojas.join(', ') : `${c.lojas.length} lojas`}
                                </option>
                            ))}
                        </select>
                    </span>
                )}
                {carregando && <Loader2 size={18} className="animate-spin" color={COR} />}

                {/* Exportação da coleta inteira — fica na barra do filtro, e não
                    dentro de um card, porque a ação é da coleta e não de uma loja. */}
                <button
                    type="button"
                    onClick={exportarExcel}
                    disabled={!lancamentos.length}
                    title={lancamentos.length
                        ? 'Baixa o lançamento de todas as lojas desta coleta num arquivo só'
                        : 'Nenhum lançamento para exportar'}
                    style={{
                        marginLeft: 'auto',
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '8px 14px', borderRadius: 8, fontSize: 13.5, fontWeight: 600,
                        border: `1px solid ${lancamentos.length ? COR : '#cbd5e1'}`,
                        background: lancamentos.length ? COR : '#f1f5f9',
                        color: lancamentos.length ? '#fff' : '#94a3b8',
                        cursor: lancamentos.length ? 'pointer' : 'not-allowed',
                    }}>
                    <Download size={16} /> Exportar lançamentos (.xlsx)
                </button>
                {algumNaoFecha && (
                    <span style={{
                        width: '100%', display: 'inline-flex', alignItems: 'center', gap: 6,
                        color: COR_ALERTA, fontSize: 13,
                    }}>
                        <AlertTriangle size={14} /> Há loja com débito e crédito diferentes nesta
                        coleta — o arquivo sai, mas confira a aba <strong>Conferência</strong> antes
                        de transcrever.
                    </span>
                )}
            </section>

            {erro && (
                <div style={{
                    display: 'flex', gap: 10, alignItems: 'center', background: '#fef2f2',
                    border: '1px solid #fecaca', color: '#991b1b', borderRadius: 10,
                    padding: '12px 16px', marginBottom: 16, fontSize: 14,
                }}>
                    <AlertTriangle size={18} /> {erro}
                </div>
            )}

            {lancamentos.map((lanc) => (
                <article key={lanc.loja} style={{
                    border: '1px solid #cbd5e1', borderRadius: 10, marginBottom: 22, overflow: 'hidden',
                }}>
                    {/* Cabeçalho no padrão do SAP */}
                    <div style={{
                        background: '#1e3a5f', color: '#fff', padding: '9px 14px', fontSize: 13.5,
                        fontWeight: 600, display: 'flex', justifyContent: 'space-between', gap: 12,
                    }}>
                        <span>Lançamento contábil manual — {lanc.loja}</span>
                        {/* A semana pode ter coletas de dias diferentes; sem a data
                            aqui, o contador não sabe de qual malote é este bloco. */}
                        {lanc.dtColeta && lanc.dtColeta !== selecionada && (
                            <span style={{ fontWeight: 500, opacity: 0.85, whiteSpace: 'nowrap' }}>
                                coleta de {dataBR(lanc.dtColeta)}
                            </span>
                        )}
                    </div>

                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))',
                        gap: 12, padding: '14px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
                    }}>
                        <Campo rotulo="Série" valor={lanc.cabecalho.serie} />
                        <Campo rotulo="Data de lançamento" valor={dataBR(lanc.cabecalho.dataLancamento)} />
                        <Campo rotulo="Origem" valor={lanc.cabecalho.origem} />
                        <Campo rotulo="Tipo lançamento ECD" valor={lanc.cabecalho.tipoLancamentoEcd} />
                        <Campo rotulo="Filial" valor={lanc.cabecalho.filial} />
                        <Campo rotulo="Observações" valor={lanc.cabecalho.observacao} destaque />
                    </div>

                    {/* A grade Débito/Crédito */}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...th, width: 34 }}>#</th>
                                    <th style={th}>Cta.contáb./cód.PN</th>
                                    <th style={th}>Cta.cont./Nome PN</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Débito</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Crédito</th>
                                    <th style={th}>Observações</th>
                                    <th style={th}>Regra distr.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lanc.linhas.map((l, i) => (
                                    <tr key={i}>
                                        <td style={{ ...td, color: COR_NEUTRA }}>{i + 1}</td>
                                        <td style={{ ...td, fontFamily: 'monospace', fontSize: 13 }}>
                                            {l.conta || <span style={{ color: COR_ALERTA }}>— preencher —</span>}
                                        </td>
                                        <td style={td}>{l.nome}</td>
                                        <td style={tdNum}>{l.debito ? dinheiro(l.debito) : ''}</td>
                                        <td style={tdNum}>{l.credito ? dinheiro(l.credito) : ''}</td>
                                        <td style={{ ...td, fontSize: 13 }}>{l.observacao}</td>
                                        <td style={td}>{l.regraDistribuicao || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: '#f8fafc' }}>
                                    <td colSpan={3} style={{ ...td, fontWeight: 700 }}>Totais</td>
                                    <td style={{ ...tdNum, fontWeight: 700 }}>{dinheiro(lanc.totais.debito)}</td>
                                    <td style={{ ...tdNum, fontWeight: 700 }}>{dinheiro(lanc.totais.credito)}</td>
                                    <td colSpan={2} style={td}>
                                        {/* O lançamento tem de fechar. Se não fecha, é erro nosso —
                                            e transcrever um lançamento desbalanceado é pior que
                                            não ter a tela. */}
                                        {lanc.fecha ? (
                                            <span style={{ color: '#15803d', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
                                                <CheckCircle2 size={14} /> Débito e crédito fecham
                                            </span>
                                        ) : (
                                            <span style={{ color: '#b91c1c', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700 }}>
                                                <AlertTriangle size={14} /> NÃO FECHA — não transcreva
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div style={{
                        padding: '10px 16px', borderTop: '1px solid #e2e8f0',
                        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                    }}>
                        {/* Escolha do período do numerário — por loja, porque cada
                            malote cobre um período diferente. A sugestão vem dos
                            envelopes; quem assina o lançamento decide. */}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13.5, color: '#334155' }}>
                            <strong style={{ fontWeight: 600 }}>Semanas de numerário:</strong>
                            <button type="button" aria-label="Menos uma semana"
                                onClick={() => mudarSemanas(lanc.loja, lanc.semanasUsadas - 1)}
                                disabled={lanc.semanasUsadas <= 1} style={passo(lanc.semanasUsadas <= 1)}>
                                <Minus size={13} />
                            </button>
                            <span style={{ minWidth: 16, textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                {lanc.semanasUsadas}
                            </span>
                            <button type="button" aria-label="Mais uma semana"
                                onClick={() => mudarSemanas(lanc.loja, lanc.semanasUsadas + 1)}
                                disabled={lanc.semanasUsadas >= 8} style={passo(lanc.semanasUsadas >= 8)}>
                                <Plus size={13} />
                            </button>
                            {/* O padrão é 1 semana. Quando os envelopes do malote
                                indicam mais, o atalho leva direto para o número
                                detectado — é o caso do FER de 08/07. */}
                            {lanc.semanasSugeridas > lanc.semanasUsadas && (
                                <button type="button"
                                    onClick={() => mudarSemanas(lanc.loja, lanc.semanasSugeridas)}
                                    title="Os envelopes deste malote são de mais de uma semana"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none',
                                        border: 'none', color: COR_ALERTA, fontSize: 12.5, cursor: 'pointer', fontWeight: 600,
                                    }}>
                                    <AlertTriangle size={12} /> os envelopes indicam {lanc.semanasSugeridas} — usar
                                </button>
                            )}
                            {lanc.semanasUsadas > 1 && (
                                <button type="button" onClick={() => voltarSugestao(lanc.loja)}
                                    title="Voltar ao padrão de uma semana"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none',
                                        border: 'none', color: COR_NEUTRA, fontSize: 12.5, cursor: 'pointer', fontWeight: 600,
                                    }}>
                                    <RotateCcw size={12} /> padrão (1)
                                </button>
                            )}
                        </span>

                        <button type="button" onClick={() => copiar(lanc)} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 7,
                            background: '#fff', border: `1px solid ${COR_BORDA}`, color: COR,
                            padding: '7px 13px', borderRadius: 8, fontWeight: 600, fontSize: 13.5, cursor: 'pointer',
                        }}>
                            <Copy size={15} /> {copiado === lanc.loja ? 'Copiado!' : 'Copiar linhas'}
                        </button>
                        {lanc.cobertura !== undefined && !lanc.confiavel && (
                            <span style={{ color: COR_ALERTA, fontSize: 13 }}>
                                ⚠️ Só {Math.round(lanc.cobertura * 100)}% dos envelopes têm lastro (Flex ou Troco) —
                                o número de semanas cobertas pode estar errado.
                            </span>
                        )}
                    </div>

                    {lanc.avisos?.length > 0 && (
                        <ul style={{
                            margin: 0, padding: '10px 16px 14px 34px', background: '#fffbeb',
                            borderTop: '1px solid #fde68a', color: '#92400e', fontSize: 13, lineHeight: 1.6,
                        }}>
                            {lanc.avisos.map((a, i) => <li key={i}>{a}</li>)}
                        </ul>
                    )}
                </article>
            ))}

            {dados && lancamentos.length === 0 && !carregando && (
                <p style={{ color: '#94a3b8', fontSize: 14 }}>
                    Nenhuma guia importada para esta coleta.
                </p>
            )}
        </div>
    );
};

const passo = (desabilitado) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 24, height: 24, borderRadius: 6, border: '1px solid #cbd5e1',
    background: '#fff', color: desabilitado ? '#cbd5e1' : COR,
    cursor: desabilitado ? 'not-allowed' : 'pointer',
});

const Campo = ({ rotulo, valor, destaque }) => (
    <div>
        <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 3 }}>{rotulo}</div>
        <div style={{
            border: '1px solid #cbd5e1', borderRadius: 4, padding: '5px 8px',
            background: destaque ? '#fefce8' : '#fff', fontSize: 13.5, color: '#0f172a',
            minHeight: 19, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{valor}</div>
    </div>
);

export default FinanceiroNumerarioSap;
